import { useMediaQuery, useTheme } from '@mui/material'
import dayjs from 'dayjs'
import { useAtomValue } from 'jotai'
import { useCallback } from 'react'
import * as React from 'react'
import { useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { viewTypeAtom } from '../../contexts/AtomUtil'
import { currentUserAtom } from '../../domains/user/AtomUsers'
import GroupSessionService from '../../services/GroupSessionService'
import { isAdmin, isOwner, isParticipant } from '../../share/utility'
import useBar from '../common/Bar'

export const useGroupSessionBooking = (session, updateHook, deleteHook, mode = 'detailed') => {
  const user = useAtomValue(currentUserAtom)

  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const bar = useBar()
  const viewType = useAtomValue(viewTypeAtom)

  const [error, setError] = useState(null)
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)

  const [registerDialogOpen, setRegisterDialogOpen] = React.useState(false)
  const registerButtonRef = React.useRef(null)
  const [editDialogOpen, setEditDialogOpen] = React.useState(false)
  const [editFormData, setEditFormData] = React.useState(null)
  const [editFormErrors, setEditFormErrors] = React.useState({})
  const [submitting, setSubmitting] = React.useState(false)
  const [unregisterDialogOpen, setUnregisterDialogOpen] = React.useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false)

  const [loading, setLoading] = React.useState(false)

  const registerAction = {
    dialogOpen: () => {
      if (!user) {
        navigate('/login', {
          state: {
            message: 'Please sign in to book a group session',
            redirectTo: location.pathname,
          },
        })
        return
      }

      setRegisterDialogOpen(true)
    },
    dialogClose: () => {
      if (registerButtonRef.current) {
        registerButtonRef.current.focus()
      }
      setRegisterDialogOpen(false)
    },
    do: async ({ notes, participants }) => {
      try {
        setLoading(true)
        await GroupSessionService.registerSession(session.resourceId, {
          participants: participants,
          notes,
        })
        bar.success('Successfully registered for session')
        setRegisterDialogOpen(false)
        updateHook?.()
      } catch (err) {
        bar.error('Failed to register for session: ' + err.message)
      } finally {
        setLoading(false)
      }
    },
  }

  const [selfRegister, setSelfRegister] = useState(false)

  const sessionData = React.useMemo(() => {
    let cleanedSession = { ...session }
    if (isOwner(user, session) || isAdmin(user)) {
      cleanedSession.participantsDetail = true
    }

    if (mode === 'small' && viewType === 'instructor') {
      let { instructorId: _instructorId, ...rest } = cleanedSession
      cleanedSession = rest
    }

    setSelfRegister(!isAdmin(user) && isParticipant(user, session))

    return cleanedSession
  }, [session, user, viewType, mode])

  const unregisterAction = {
    dialogOpen: async () => {
      const editable = isAdmin(user) || isOwner(user, session)
      if (editable) {
        setUnregisterDialogOpen(true)
        return
      }

      if (session.cancellationDeadline) {
        const now = dayjs()
        const cancellationDeadline = dayjs(session.cancellationDeadline)
        if (now.isAfter(cancellationDeadline)) {
          bar.error('Cancellation deadline has passed. If you have an emergency, please contact the instructor directly.')
          return
        }
      }

      setUnregisterDialogOpen(true)
    },
    dialogClose: () => {
      setUnregisterDialogOpen(false)
    },
    do: async (participants) => {
      if (participants.length === 0) {
        bar.warn('Please select participants to unregister')
        return
      }

      try {
        await GroupSessionService.unregisterSession(session.resourceId, { participants })
        bar.success('Successfully unregistered participants')
        setUnregisterDialogOpen(false)
        if (updateHook) {
          updateHook()
        }
      } catch (err) {
        console.error('Failed to unregister participants:', err)
        bar.error(err.message || 'Failed to unregister participants')
      }
    },
  }

  const deleteAction = {
    dialogOpen: () => setDeleteDialogOpen(true),
    dialogClose: () => setDeleteDialogOpen(false),
    do: async () => {
      try {
        await GroupSessionService.deleteGroupSession(session.resourceId)
        bar.success('Successfully deleted session')
        setDeleteDialogOpen(false)
        deleteHook?.()
      } catch (err) {
        console.error('Failed to delete session:', err)
        bar.error(err.message || 'Failed to delete session')
      }
    },
  }

  const handleEditClose = () => {
    setEditDialogOpen(false)
    setEditFormData(null)
    setEditFormErrors({})
  }

  const validateEditForm = (data) => {
    const errors = {}
    if (!data.title) errors.title = 'Title is required'
    if (!data.sport) errors.sport = 'Sport type is required'
    if (!data.skiResort) errors.skiResort = 'Location is required'
    if (!data.price || data.price < 0) errors.price = 'Price must be greater than 0'
    if (!data.minParticipants || !data.maxParticipants || data.maxParticipants < data.minParticipants) {
      errors.maxParticipants = 'Maximum participants must be greater than minimum'
    }
    return errors
  }

  const editAction = {
    dialogOpen: () => {
      if (isMobile) {
        navigate(`/events/sessions/${session.resourceId}/edit`)
        return
      }
      setEditFormData(session)
      setEditDialogOpen(true)
    },
    dialogClose: handleEditClose,
    do: async () => {
      const errors = validateEditForm(editFormData)
      if (Object.keys(errors).length > 0) {
        setEditFormErrors(errors)
        bar.error('Please fix the errors before submitting')
        return
      }

      try {
        setSubmitting(true)
        await GroupSessionService.updateGroupSession(session.resourceId, editFormData)
        bar.success('Successfully updated session')
        handleEditClose()
        updateHook?.()
      } catch (err) {
        console.error('Failed to update session:', err)
        bar.error(err.message || 'Failed to update session')
      } finally {
        setSubmitting(false)
      }
    },
  }

  const onFormDataChange = useCallback((data) => {
    setEditFormData((prev) => {
      return { ...prev, ...data }
    })
  }, [])

  return {
    registerAction,
    unregisterAction,
    deleteAction,
    editAction,

    registerDialogOpen,
    setRegisterDialogOpen,
    editDialogOpen,
    setEditDialogOpen,
    submitting,
    setSubmitting,
    unregisterDialogOpen,
    setUnregisterDialogOpen,
    deleteDialogOpen,
    setDeleteDialogOpen,
    loading,
    setLoading,
    error,
    setError,

    editFormData,
    setEditFormData,
    onFormDataChange,
    editFormErrors,
    setEditFormErrors,
    openConfirmDialog,
    setOpenConfirmDialog,

    selfRegister,
    sessionData,
  }
}

export default useGroupSessionBooking
